import { PreconditionFailedError } from "../errors";
import type { ProcedureTrial } from "../procedure-trial.model";
import { answersEqual, type ParameterAnswer } from "../value.model";
import type { Brand } from "./brand";

export type PlannedTrialId = Brand<string, "PlannedTrialId">;

/**
 * One administration planned ahead (ADR-0024, Rule 7): a plan, never a quota.
 * It answers some or all of the definition's parameters.
 *
 * The id is minted when the row is created and copied unchanged from protocol
 * to order, so a row can be edited, reordered or removed by identity rather
 * than position.
 */
export interface PlannedTrial {
  id: PlannedTrialId;
  values: readonly ParameterAnswer[];
}

export function asPlannedTrialId(value: string): PlannedTrialId {
  if (!value) throw new PreconditionFailedError("A planned trial identity cannot be empty");
  return value as PlannedTrialId;
}

/** Why a list of planned trials is malformed; the owning aggregate raises its own error. */
export function plannedTrialsProblem(trials: readonly PlannedTrial[]): string | undefined {
  const ids = new Set<string>();
  for (const trial of trials) {
    if (ids.has(trial.id)) return `Planned trial '${trial.id}' appears more than once`;
    ids.add(trial.id);
    const answered = new Set<string>();
    for (const answer of trial.values) {
      const key = `${answer.source}\0${answer.parameterId}`;
      if (answered.has(key)) return `Planned trial '${trial.id}' answers parameter '${answer.parameterId}' twice`;
      answered.add(key);
    }
  }
  return undefined;
}

export function plannedTrialsEqual(a: readonly PlannedTrial[], b: readonly PlannedTrial[]): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function matchingPlannedTrial(
  trials: readonly PlannedTrial[],
  answers: readonly ParameterAnswer[],
): PlannedTrial | undefined {
  return trials
    .filter(
      (trial) =>
        trial.values.length > 0 &&
        trial.values.every((planned) =>
          answers.some(
            (answer) =>
              answer.source === planned.source &&
              answer.parameterId === planned.parameterId &&
              answersEqual(answer.value, planned.value),
          ),
        ),
    )
    .sort((a, b) => b.values.length - a.values.length)[0];
}

/** Live trial → the planned trial it was run from. Only explicit links count, and several runs may share one. */
export function fulfilledPlannedTrials(
  planned: readonly PlannedTrial[],
  recorded: readonly ProcedureTrial[],
): Map<string, PlannedTrialId> {
  const result = new Map<string, PlannedTrialId>();
  for (const trial of recorded) {
    if (trial.lifecycle.status === "EnteredInError" || !trial.plannedTrialId) continue;
    if (planned.some((plan) => plan.id === trial.plannedTrialId)) result.set(trial.id, trial.plannedTrialId);
  }
  return result;
}
